'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import { client, MONTH_ACTIVITIES, monthAssignee } from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, fieldClasses, FieldLabel } from './app-screen'
import styles from './recurring-visual.module.css'
import { useScenario } from './use-scenario'

const RECURRING_CLIENT_NR = '10010'
const RECURRING_ACTIVITY = MONTH_ACTIVITIES[1]

/** Read off the shared month planning rather than picked here: on that board this client's VAT
 *  return is Nina's, so the dialog that sets the task up hands it to the same colleague. */
const HANDLER = monthAssignee(RECURRING_CLIENT_NR, RECURRING_ACTIVITY.key)

// Quarterly occurrences (0-indexed months): jan, apr, jul, okt.
const QUARTER_MONTHS = new Set([0, 3, 6, 9])

const TOTAL_BEATS = 11
const REST_BEAT = 8 // SSR / reduced-motion / not-yet-playing snapshot — all fields filled, dots on
// Beat 9 renders identically to the rest frame: without that hold the quarterly dots landed at 7
// and the whole form cleared at 9, leaving barely 1.5s of settled payoff.
const RESET_BEAT = 10 // final loop beat: already reads as the empty starting state

const DROPDOWN_WINDOW: readonly [number, number] = [1, 3]
const HIGHLIGHT_AT = 2
const CHOSEN_FROM = 3
const UREN_FROM = 4
const PRIJS_FROM = 5
const BEHANDELAAR_FROM = 6
const DOTS_FROM = 7

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive field state;
 *  the final loop beat already reads as the sparse start, for a seamless wrap. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/** Crossfades between a muted placeholder and the filled value, stacked so neither shifts layout. */
function ValueSlot({ visible, placeholder, value }: { visible: boolean; placeholder: ReactNode; value: ReactNode }) {
  return (
    <span className="relative flex min-w-0 items-center">
      <span className={clsx(styles.placeholder, visible && styles.placeholderHidden, 'truncate')}>{placeholder}</span>
      <span
        className={clsx(styles.fieldValue, visible && styles.fieldValueVisible, 'absolute inset-0 flex items-center')}
      >
        {value}
      </span>
    </span>
  )
}

/**
 * Grip card 1 — "Planning wijzigen" dialog: setting up a quarterly recurring task. The Frequentie
 * dropdown opens, "Per kwartaal" highlights and is chosen, Uren/Prijs/Behandelaar fill in, then the
 * 4 quarterly timeline dots pop onto the axis. Driven by `useScenario`'s 13-beat loop; `beat ===
 * null` (SSR, no-JS, `prefers-reduced-motion: reduce`) renders the resolved rest state: all fields
 * filled, dots on the timeline, no dropdown.
 */
export function RecurringVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 900 })
  const effBeat = effectiveBeat(beat)

  const dropdownOpen = effBeat >= DROPDOWN_WINDOW[0] && effBeat < DROPDOWN_WINDOW[1]
  const highlightChosen = effBeat === HIGHLIGHT_AT
  const chosen = effBeat >= CHOSEN_FROM
  const urenFilled = effBeat >= UREN_FROM
  const prijsFilled = effBeat >= PRIJS_FROM
  const behandelaarFilled = effBeat >= BEHANDELAAR_FROM
  const dotsVisible = effBeat >= DOTS_FROM

  return (
    <AppUiVisual label={t.screen.recurring.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="recurring" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.recurring.changePlanning}
            detail={client(RECURRING_CLIENT_NR).name}
            right={
              <span className="text-[11px]" style={{ color: 'var(--au-text2)' }}>
                ✕
              </span>
            }
          />

          {/* Centred, with a fixed rhythm between the three groups. The leftover height is split
              above and below the form rather than handed to the gaps, which is what pushed the
              groups so far apart they stopped reading as one form. */}
          <div className={clsx(styles.form, 'flex flex-1 flex-col justify-center px-3 py-2')}>
            {/* The activity being set up. A VAT return is the textbook recurring task — it is filed
                every quarter — so the frequency chosen below actually follows from it. */}
            <div
              className="flex items-center gap-2.5 rounded-[7px] px-2.5 py-2.5"
              style={{ background: 'var(--au-accent-soft)' }}
            >
              <span
                className="flex size-[19px] shrink-0 items-center justify-center rounded-[5px] text-[10px] font-bold text-white"
                style={{ background: 'var(--au-accent)' }}
              >
                ✓
              </span>
              <span className="truncate text-[11.5px] font-semibold" style={{ color: 'var(--au-text)' }}>
                {t.screen.recurring.fileReturn(t.monthActivity[RECURRING_ACTIVITY.key].fullLabel)}
              </span>
            </div>

            <div className={clsx(styles.fieldWrap, 'flex flex-col gap-0.5')}>
              <FieldLabel roomy>{t.word.frequency}</FieldLabel>
              <div
                className={clsx(
                  fieldClasses.box,
                  fieldClasses.boxRoomy,
                  styles.select,
                  'flex items-center justify-between',
                  chosen && fieldClasses.filled,
                )}
              >
                <ValueSlot
                  visible={chosen}
                  placeholder={
                    <span className="text-[12.5px] font-medium" style={{ color: 'var(--au-text2)' }}>
                      {t.screen.recurring.chooseFrequency}
                    </span>
                  }
                  value={
                    <span className="text-[12.5px] font-semibold" style={{ color: 'var(--au-accent)' }}>
                      {t.screen.recurring.quarterly}
                    </span>
                  }
                />
                <span className="shrink-0 text-[9px] leading-none" style={{ color: 'var(--au-text2)' }}>
                  ▾
                </span>
              </div>
              <div className={clsx(styles.dropdown, dropdownOpen && styles.dropdownOpen)} aria-hidden="true">
                {t.screen.recurring.frequencies.map((option) => (
                  <div
                    key={option}
                    className={clsx(
                      styles.option,
                      option === t.screen.recurring.quarterly && highlightChosen && styles.optionHighlighted,
                    )}
                  >
                    {option}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-1.5">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <FieldLabel roomy>{t.word.hours}</FieldLabel>
                <div
                  className={clsx(
                    fieldClasses.box,
                    fieldClasses.boxRoomy,
                    'font-medium',
                    urenFilled && fieldClasses.filled,
                  )}
                  style={{ color: 'var(--au-text)' }}
                >
                  <ValueSlot
                    visible={urenFilled}
                    placeholder={<span style={{ color: 'var(--au-text2)' }}>—</span>}
                    value="1,50"
                  />
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <FieldLabel roomy>{t.word.price}</FieldLabel>
                <div
                  className={clsx(
                    fieldClasses.box,
                    fieldClasses.boxRoomy,
                    'font-medium',
                    prijsFilled && fieldClasses.filled,
                  )}
                  style={{ color: 'var(--au-text)' }}
                >
                  <ValueSlot
                    visible={prijsFilled}
                    placeholder={<span style={{ color: 'var(--au-text2)' }}>—</span>}
                    value="€ 85,00"
                  />
                </div>
              </div>
              <div className="flex min-w-0 flex-[1.4] flex-col gap-0.5">
                <FieldLabel roomy>{t.word.owner}</FieldLabel>
                <div
                  className={clsx(fieldClasses.box, fieldClasses.boxRoomy, behandelaarFilled && fieldClasses.filled)}
                >
                  <ValueSlot
                    visible={behandelaarFilled}
                    placeholder={
                      <span className="text-[11px] font-medium" style={{ color: 'var(--au-text2)' }}>
                        —
                      </span>
                    }
                    value={
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span
                          className="flex size-[16px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white"
                          style={{ background: HANDLER.color }}
                        >
                          {HANDLER.initials}
                        </span>
                        {/* Same 11px as the Uren and Prijs values beside it: a name is a field value
                            like any other, and shrinking it made this box read as a caption. */}
                        <span className="truncate font-medium" style={{ color: 'var(--au-text)' }}>
                          {HANDLER.name}
                        </span>
                      </span>
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* One column per month, so the dot and the label of a given month are the same column
              and therefore always line up. Laying these out as independent rows is what previously
              left the quarter markers floating between the dots they belonged to. */}
          {/* The strip takes the height the form no longer spends on its own gaps, so the leftover
              reads as air around the timeline rather than as a gap above it. */}
          <div className={clsx(styles.axisStrip, 'border-t px-3')} style={{ borderColor: 'var(--au-hairline)' }}>
            {/* The recurrence is named once, above the axis, and then marked four times on it.
                The name used to be a chip inside the January column, and only there: four copies of
                a word this long are each wider than the one-twelfth column they belong to, so they
                ran off the card and collided on mobile. Marking only the first quarter left the
                other three as bare dots — the axis said something recurs, not what. Off the grid the
                label has the full width, and every quarter can be marked identically.
                Both halves are the choice made in the dialog above, read back: the activity being
                set up, and the frequency just picked. */}
            <div
              className={clsx(styles.legend, dotsVisible && styles.legendVisible, 'mb-1.5 flex items-center gap-1.5')}
            >
              <span className={styles.legendDot} aria-hidden="true" />
              <span className={styles.legendLabel}>{t.monthActivity[RECURRING_ACTIVITY.key].label}</span>
              <span className={styles.legendValue}>{t.screen.recurring.quarterly}</span>
            </div>
            <div className="grid grid-cols-12">
              {t.monthsShort.map((month, index) => {
                const isQuarter = QUARTER_MONTHS.has(index)
                const marked = isQuarter && dotsVisible
                return (
                  <div key={month} className="flex min-w-0 flex-col items-center">
                    {/* The axis rule runs behind the dots; each column draws its own segment so the
                        dot sits exactly on the line at the column's centre. */}
                    <span className="relative flex h-[13px] w-full items-center justify-center">
                      <span className="absolute inset-x-0 h-px" style={{ background: 'var(--au-hairline)' }} />
                      <span
                        className={clsx(styles.axisDot, marked && styles.axisDotActive, 'relative')}
                        style={isQuarter ? { transitionDelay: `${index * 20}ms` } : undefined}
                      />
                    </span>
                    {/* The month's own name is the marker. Three letters fit the column at any width,
                        where the activity's name never could, so all four quarters get the same
                        treatment and the axis reads as four scheduled months rather than one
                        labelled one and three anonymous dots. */}
                    <span
                      className={clsx(styles.monthLabel, marked && styles.monthLabelMarked, 'mt-1')}
                      style={isQuarter ? { transitionDelay: `${index * 20}ms` } : undefined}
                    >
                      {month}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
